import "@xyflow/react/dist/style.css";
import "./workflow.scss";
import {
  Background,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import { WorkflowContext, edgeTypes, nodeTypes } from "./WorkflowNodes";
import {
  BRANCH_COLORS,
  ICONS,
  computeStatuses,
  flowToSave,
  makeNode,
  newId,
  tidyPositions,
  wouldLoop,
} from "./workflowGraph";

/**
 * Workflow builder (user, 2026-10-29, second version): Sales -> Utilities ->
 * Products -> Workflow -> Details. The workflow is drawn top to bottom as in
 * the boss's pictures: Trigger (Order Approved) -> steps joined by arrows ->
 * branches side by side -> Ready for Production once every arrow into it is
 * done. The left panel lists every Station (Products -> Workflow ->
 * Stations); dragging one in adds it as a block, as often as needed
 * (user, 2026-10-30). Order Approved and Ready for Production are always
 * there. "Test run" tries the Done / Undo rules and is not saved.
 */

const LIST_URL = "/sales/products?active=workflow";
const DND_TYPE = "application/x-nova-workflow";

const edgeLook = {
  type: "removable",
  markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: "#9ca3af" },
  style: { strokeWidth: 1.5, stroke: "#9ca3af" },
};

// Only stations are dragged in (user, 2026-11-02: no branch / milestone
// blocks; old ones in a saved workflow still show and can be deleted).
// Order Approved and Ready for Production are added by the builder, one
// each, and cannot be deleted.
const FIXED_TYPES = ["trigger", "ready"];
const STATION_DRAG = "station:";

function Builder() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { screenToFlowPosition, fitView } = useReactFlow();

  const [workflow, setWorkflow] = useState(null);
  const [stationList, setStationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [dirty, setDirty] = useState(false);
  const [mode, setMode] = useState("build");
  const [doneIds, setDoneIds] = useState(() => new Set());

  const notify = useCallback(
    (message, type = "success") => dispatch(setNotification({ message, type })),
    [dispatch],
  );

  const loadStations = useCallback(async () => {
    const { data } = await API.get("/sales/stations/all", { headers: authHeader() });
    const list = data?.data || [];
    setStationList(list);
    return list;
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([API.get(`/sales/workflow/${id}`, { headers: authHeader() }), loadStations()])
      .then(([{ data }]) => {
        const w = data?.data;
        setWorkflow(w);
        let ns = w?.flow?.nodes || [];
        // Every workflow starts at Order Approved and ends at Ready.
        if (!ns.some((n) => n.type === "trigger")) ns = [makeNode("trigger", { x: 0, y: 0 }), ...ns];
        if (!ns.some((n) => n.type === "ready")) ns = [...ns, makeNode("ready", { x: 0, y: 370 })];
        setNodes(ns);
        setEdges((w?.flow?.edges || []).map((e) => ({ ...e, ...edgeLook })));
        setTimeout(() => fitView({ padding: 0.15, maxZoom: 1 }), 50);
      })
      .catch((err) => notify(err?.response?.data?.message || err.message, "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Warn before leaving the page with unsaved changes.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const stations = useMemo(() => new Map(stationList.map((s) => [Number(s.id), s])), [stationList]);
  const statuses = useMemo(() => computeStatuses(nodes, edges, doneIds), [nodes, edges, doneIds]);
  const selected = mode === "build" ? nodes.filter((n) => n.selected) : [];
  const editing = selected.length === 1 ? selected[0] : null;

  const ctx = useMemo(
    () => ({
      mode,
      statuses,
      stations,
      setDone: (nodeId, isDone) =>
        setDoneIds((cur) => {
          const next = new Set(cur);
          if (isDone) next.add(nodeId);
          else next.delete(nodeId);
          return next;
        }),
      removeEdge: (edgeId) => {
        setEdges((es) => es.filter((e) => e.id !== edgeId));
        setDirty(true);
      },
    }),
    [mode, statuses, stations, setEdges],
  );

  const updateData = (nodeId, patch) => {
    setNodes((ns) => ns.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n)));
    setDirty(true);
  };

  const removeNodes = (ids) => {
    const gone = new Set(ids);
    setNodes((ns) => ns.filter((n) => !gone.has(n.id)));
    setEdges((es) => es.filter((e) => !gone.has(e.source) && !gone.has(e.target)));
    setDirty(true);
  };

  const handleNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes);
      if (changes.some((c) => (c.type === "position" && c.dragging === false) || c.type === "remove")) {
        setDirty(true);
      }
    },
    [onNodesChange],
  );

  const handleEdgesChange = useCallback(
    (changes) => {
      onEdgesChange(changes);
      if (changes.some((c) => c.type === "remove")) setDirty(true);
    },
    [onEdgesChange],
  );

  const isValidConnection = useCallback(
    (c) =>
      !wouldLoop(edges, c.source, c.target) &&
      !edges.some((e) => e.source === c.source && e.target === c.target),
    [edges],
  );

  const onConnect = useCallback(
    (c) => {
      setEdges((es) => addEdge({ ...c, id: newId("e"), ...edgeLook }, es));
      setDirty(true);
    },
    [setEdges],
  );

  // The Delete key removes lines and blocks, never the start or the end.
  const onBeforeDelete = useCallback(
    async ({ nodes: goneNodes, edges: goneEdges }) => ({
      nodes: goneNodes.filter((n) => !FIXED_TYPES.includes(n.type)),
      edges: goneEdges,
    }),
    [],
  );

  const onDrop = (e) => {
    if (mode !== "build") return;
    const type = e.dataTransfer.getData(DND_TYPE);
    if (!type) return;
    e.preventDefault();
    const p = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    // Centre the 220px block on the pointer. A station comes in as a step
    // at that station.
    const at = { x: p.x - 110, y: p.y - 30 };
    if (!type.startsWith(STATION_DRAG)) return;
    const made = makeNode("step", at, { stationId: Number(type.slice(STATION_DRAG.length)) });
    const node = { ...made, selected: true };
    setNodes((ns) => [...ns.map((n) => ({ ...n, selected: false })), node]);
    setDirty(true);
  };

  const tidy = () => {
    const pos = tidyPositions(nodes, edges);
    setNodes((ns) => ns.map((n) => ({ ...n, position: pos.get(n.id) || n.position })));
    setDirty(true);
    setTimeout(() => fitView({ padding: 0.15, maxZoom: 1, duration: 300 }), 50);
  };

  const save = async () => {
    try {
      setLoading(true);
      const { data } = await API.patch(
        "/sales/workflow",
        { id: workflow.id, flow: flowToSave(nodes, edges) },
        { headers: authHeader() },
      );
      if (data?.success === false) throw new Error(data?.message);
      setDirty(false);
      notify(data?.message || "Workflow saved");
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const stepCounts = useMemo(() => {
    let done = 0;
    let total = 0;
    nodes.forEach((n) => {
      if (n.type !== "step") return;
      total += 1;
      if (statuses.get(n.id)?.status === "done") done += 1;
    });
    return { done, total };
  }, [nodes, statuses]);

  return (
    <div className="wf-page">
      {loading && <Spinner />}
      <div className="wf-toolbar">
        <Link
          to={LIST_URL}
          className="btn btn-sm bg-white border"
          onClick={(e) => {
            if (dirty && !window.confirm("Leave without saving your changes?")) e.preventDefault();
          }}
        >
          <i className="mdi mdi-arrow-left" /> Workflows
        </Link>
        <div className="ms-2">
          <div className="fw-bold">{workflow?.productTypeName || "Workflow"}</div>
          <small className="text-muted">Workflow</small>
        </div>
        <div className="btn-group btn-group-sm ms-3">
          <button
            type="button"
            className={`btn ${mode === "build" ? "btn-primary" : "bg-white border"}`}
            onClick={() => setMode("build")}
          >
            <i className="mdi mdi-pencil-ruler" /> Build
          </button>
          <button
            type="button"
            className={`btn ${mode === "test" ? "btn-primary" : "bg-white border"}`}
            onClick={() => {
              setNodes((ns) => ns.map((n) => ({ ...n, selected: false })));
              setMode("test");
            }}
          >
            <i className="mdi mdi-play" /> Test run
          </button>
        </div>
        {mode === "test" && (
          <>
            <span className="ms-3 small">
              {stepCounts.done} of {stepCounts.total} steps done
            </span>
            <button type="button" className="btn btn-sm bg-white border ms-2" onClick={() => setDoneIds(new Set())}>
              Reset
            </button>
            <small className="text-muted ms-2">Try-out only, not saved.</small>
          </>
        )}
        <div className="ms-auto d-flex align-items-center gap-2">
          {mode === "build" && (
            <button type="button" className="btn btn-sm bg-white border" onClick={tidy} title="Line the blocks up">
              <i className="mdi mdi-auto-fix" /> Tidy
            </button>
          )}
          {dirty && <span className="badge bg-warning text-dark">Unsaved changes</span>}
          <button type="button" className="btn btn-primary btn-sm" disabled={!dirty || !workflow} onClick={save}>
            <i className="mdi mdi-content-save" /> Save
          </button>
        </div>
      </div>

      <div className="wf-body">
        {mode === "build" && (
          <div className="wf-palette">
            <div className="wf-side-title">Stations</div>
            {stationList.map((s) => (
              <div
                key={s.id}
                className="wf-palette-item wf-palette-station"
                draggable
                title={s.description || undefined}
                onDragStart={(e) => {
                  e.dataTransfer.setData(DND_TYPE, `${STATION_DRAG}${s.id}`);
                  e.dataTransfer.effectAllowed = "copy";
                }}
              >
                <i className={`mdi mdi-${s.icon || "checkbox-blank-outline"} me-1`} />
                <b>{s.name}</b>
              </div>
            ))}
            {!stationList.length && (
              <small className="text-muted d-block mb-2">
                No stations yet. Add them in{" "}
                <Link to="/sales/products?active=stations">Stations</Link>.
              </small>
            )}
            <div className="wf-help small text-muted mt-3">
              <p className="mb-1">
                <b>Add:</b> drag a station onto the canvas; the same station can go in more than once.
              </p>
              <p className="mb-1">
                <b>Join:</b> drag from a block's bottom dot to the next block's top dot.
              </p>
              <p className="mb-1">
                <b>Edit:</b> click a block; its details show on the right.
              </p>
              <p className="mb-0">
                <b>Remove a line:</b> click it, then the red &times; on it (or press Delete).
              </p>
              <p className="mb-0">
                <b>Remove a block:</b> click it, then press Delete.
              </p>
            </div>
          </div>
        )}

        <div className="wf-canvas" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
          <WorkflowContext.Provider value={ctx}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodesChange={handleNodesChange}
              onEdgesChange={handleEdgesChange}
              onConnect={onConnect}
              isValidConnection={isValidConnection}
              onBeforeDelete={onBeforeDelete}
              deleteKeyCode={mode === "build" ? ["Delete", "Backspace"] : null}
              nodesDraggable={mode === "build"}
              nodesConnectable={mode === "build"}
              // Kept on in the Test run too: with nodes neither draggable nor
              // selectable React Flow makes them ignore the mouse, which
              // would switch off the Done / Undo buttons.
              elementsSelectable
              minZoom={0.2}
              maxZoom={1.5}
            >
              <Background gap={18} />
              <Controls showInteractive={false} />
              <MiniMap pannable zoomable />
            </ReactFlow>
          </WorkflowContext.Provider>
        </div>

        {mode === "build" && (
          <div className="wf-props">
            {!editing ? (
              <div className="text-muted small">
                {selected.length > 1 ? `${selected.length} blocks selected.` : "Click a block to edit it."}
              </div>
            ) : (
              <BlockForm
                node={editing}
                stationList={stationList}
                onChange={(patch) => updateData(editing.id, patch)}
                onDelete={() => removeNodes([editing.id])}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const TYPE_LABEL = {
  trigger: "Trigger",
  step: "Station",
  branch: "Branch heading",
  milestone: "Milestone",
  ready: "Ready for Production",
};

// The right-hand panel: the selected block's fields.
function BlockForm({ node, stationList, onChange, onDelete }) {
  const { type, data } = node;
  return (
    <div>
      <div className="wf-side-title">{TYPE_LABEL[type]}</div>
      {type === "step" && (
        <div className="fw-bold mb-2">
          {stationList.find((s) => Number(s.id) === Number(data.stationId))?.name || (
            <span className="text-danger">Station removed</span>
          )}
        </div>
      )}
      <label className="form-label small mb-0">{type === "step" ? "Label" : "Name"}</label>
      <input
        className="form-control form-control-sm mb-2"
        value={data.name || ""}
        maxLength={100}
        disabled={type === "trigger"}
        placeholder={type === "step" ? "e.g. Order Shirts" : undefined}
        onChange={(e) => onChange({ name: e.target.value })}
      />
      {(type === "step" || type === "ready") && (
        <>
          <label className="form-label small mb-0">Note</label>
          <input
            className="form-control form-control-sm mb-2"
            value={data.note || ""}
            maxLength={150}
            placeholder="Optional, shown under the name"
            onChange={(e) => onChange({ note: e.target.value })}
          />
        </>
      )}
      {type === "branch" && (
        <>
          <label className="form-label small mb-0">Colour</label>
          <div className="d-flex gap-1 mb-2">
            {Object.entries(BRANCH_COLORS).map(([key, c]) => (
              <button
                key={key}
                type="button"
                title={key}
                className={`wf-swatch ${data.color === key ? "wf-swatch-on" : ""}`}
                style={{ background: c.bg, borderColor: c.fg }}
                onClick={() => onChange({ color: key })}
              />
            ))}
          </div>
          <label className="form-label small mb-0">Icon</label>
          <div className="wf-icon-grid mb-2">
            {["source-branch", ...ICONS].map((icon) => (
              <button
                key={icon}
                type="button"
                className={`btn btn-sm ${data.icon === icon ? "btn-primary" : "bg-white border"}`}
                onClick={() => onChange({ icon })}
              >
                <i className={`mdi mdi-${icon}`} />
              </button>
            ))}
          </div>
        </>
      )}
      {type === "trigger" ? (
        <small className="text-muted">Every workflow starts here, when the W-Order is approved.</small>
      ) : type === "ready" ? (
        <small className="text-muted">Every workflow ends here.</small>
      ) : (
        <button type="button" className="btn btn-sm btn-outline-danger mt-2" onClick={onDelete}>
          <i className="mdi mdi-delete" /> Delete block
        </button>
      )}
    </div>
  );
}

export default function WorkflowBuilder() {
  return (
    <ReactFlowProvider>
      <Builder />
    </ReactFlowProvider>
  );
}
